import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import './AdmissionControls.css';

// The schedule inputs are datetime-local values, interpreted as UTC (the timezone this page displays).
const parseUtc = (value) => new Date(`${value}:00Z`);

const AdmissionControls = () => {
  const [settings, setSettings] = useState({
    is_enabled: true,
    use_schedule: false,
    start_date: '',
    end_date: '',
    show_countdown: true,
    announcement_message: '',
    timezone: 'UTC'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewStatus, setPreviewStatus] = useState(null);
  const { isViewer } = useAuth();
  const { settings: school, settingsLoaded, grades, gradesError, refresh } = useSchool();
  const [academicYear, setAcademicYear] = useState('');
  const [eligible, setEligible] = useState(new Set());
  const [ready, setReady] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Academic year and eligible grades come from the shared school context; seed the form once, and only from
  // real database values: seeding from the built-in fallback would let a save erase the stored academic year.
  useEffect(() => {
    if (seeded.current || grades === null || !settingsLoaded) return;
    seeded.current = true;
    setAcademicYear(school.academic_year || '');
    setEligible(new Set(grades.filter((g) => g.is_eligible).map((g) => g.key)));
    setReady(true);
  }, [grades, settingsLoaded, school.academic_year]);

  const toggleGrade = (key) =>
    setEligible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setGroup = (program, on) =>
    setEligible((prev) => {
      const next = new Set(prev);
      grades.filter((g) => g.program === program).forEach((g) => (on ? next.add(g.key) : next.delete(g.key)));
      return next;
    });

  const fetchSettings = async () => {
    try {
      const { data: s, error } = await supabase
        .from('admission_settings')
        .select('*')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      if (s) {
        setSettings({
          is_enabled: s.is_enabled,
          use_schedule: s.use_schedule,
          start_date: s.start_date ? new Date(s.start_date).toISOString().slice(0, 16) : '',
          end_date: s.end_date ? new Date(s.end_date).toISOString().slice(0, 16) : '',
          show_countdown: s.show_countdown,
          announcement_message: s.announcement_message || '',
          timezone: s.timezone || 'UTC'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const updatePreview = useCallback(() => {
    const now = new Date();
    let status = 'open';
    let remainingDays = null;

    if (settings.use_schedule && settings.start_date && settings.end_date) {
      const startDate = parseUtc(settings.start_date);
      const endDate = parseUtc(settings.end_date);

      if (now < startDate) {
        status = 'upcoming';
        remainingDays = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
      } else if (now >= startDate && now <= endDate) {
        status = 'open';
        remainingDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      } else {
        status = 'closed';
      }
    } else {
      status = settings.is_enabled ? 'open' : 'closed';
    }

    setPreviewStatus({
      status,
      remainingDays,
      message: settings.announcement_message
    });
  }, [settings]);

  useEffect(() => {
    updatePreview();
  }, [updatePreview]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      // Validation
      if (settings.use_schedule) {
        if (!settings.start_date || !settings.end_date) {
          setMessage({ type: 'error', text: 'Start and end dates are required when using schedule' });
          setSaving(false);
          return;
        }

        const startDate = parseUtc(settings.start_date);
        const endDate = parseUtc(settings.end_date);

        if (startDate >= endDate) {
          setMessage({ type: 'error', text: 'End date must be after start date' });
          setSaving(false);
          return;
        }
      }

      if (eligible.size === 0) {
        setMessage({ type: 'error', text: 'Select at least one eligible grade' });
        setSaving(false);
        return;
      }

      // One server-side call updates the settings, the academic year and the eligible grades together.
      const { error } = await supabase.rpc('save_admission_controls', {
        p: {
          is_enabled: settings.is_enabled,
          use_schedule: settings.use_schedule,
          start_date: settings.use_schedule ? parseUtc(settings.start_date).toISOString() : null,
          end_date: settings.use_schedule ? parseUtc(settings.end_date).toISOString() : null,
          show_countdown: settings.show_countdown,
          announcement_message: settings.announcement_message || null,
          academic_year: academicYear || null,
          eligible_grades: Array.from(eligible)
        }
      });
      if (error) {
        const known = { '42501': 'You do not have permission to change admission settings', '22023': error.message };
        const err = new Error(known[error.code] || 'Failed to save settings');
        throw err;
      }
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
      fetchSettings(); // Refresh
      refresh(); // Public pages read the same values through the school context
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to save settings'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">Loading admission controls...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div className="admission-controls">
      <motion.div
        className="controls-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1>Admission Controls</h1>
        <p className="subtitle">Manage when admissions are open and how they're displayed</p>

        {message.text && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="settings-grid">
          <div className="settings-panel">
            <h2>Basic Settings</h2>
            {isViewer && (
              <div className="alert alert-info">You can view these settings but not change them.</div>
            )}
            <fieldset disabled={isViewer} className="settings-fieldset">

            <div className="form-group">
              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.is_enabled}
                    onChange={(e) => setSettings({ ...settings, is_enabled: e.target.checked })}
                    disabled={settings.use_schedule}
                  />
                  <span className="toggle-slider"></span>
                  <span>Enable Admissions {settings.use_schedule && '(Controlled by schedule)'}</span>
                </label>
              </div>
              <p className="help-text">
                {settings.use_schedule 
                  ? 'Admissions will be automatically enabled/disabled based on schedule'
                  : 'Manually control whether admissions are open or closed'}
              </p>
            </div>

            <div className="form-group">
              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.use_schedule}
                    onChange={(e) => setSettings({ ...settings, use_schedule: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                  <span>Use Scheduled Dates</span>
                </label>
              </div>
              <p className="help-text">Automatically open/close based on date range</p>
            </div>

            {settings.use_schedule && (
              <div className="schedule-fields">
                <div className="form-group">
                  <label>Start Date & Time (UTC)</label>
                  <input
                    type="datetime-local"
                    value={settings.start_date}
                    onChange={(e) => setSettings({ ...settings, start_date: e.target.value })}
                    required={settings.use_schedule}
                  />
                </div>

                <div className="form-group">
                  <label>End Date & Time (UTC)</label>
                  <input
                    type="datetime-local"
                    value={settings.end_date}
                    onChange={(e) => setSettings({ ...settings, end_date: e.target.value })}
                    required={settings.use_schedule}
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <div className="toggle-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={settings.show_countdown}
                    onChange={(e) => setSettings({ ...settings, show_countdown: e.target.checked })}
                  />
                  <span className="toggle-slider"></span>
                  <span>Show Countdown Timer</span>
                </label>
              </div>
              <p className="help-text">Display remaining days on public page</p>
            </div>

            <div className="form-group">
              <label>Announcement Message</label>
              <textarea
                value={settings.announcement_message}
                onChange={(e) => setSettings({ ...settings, announcement_message: e.target.value })}
                placeholder="Enter a custom message to display on the admissions page..."
                rows={4}
              />
              <p className="help-text">This message appears on the public admissions page</p>
            </div>

            <div className="form-group">
              <label htmlFor="academic-year">Academic Year</label>
              <input
                id="academic-year"
                type="text"
                value={academicYear}
                maxLength={20}
                placeholder="For example 2026-27"
                onChange={(e) => setAcademicYear(e.target.value)}
              />
              <p className="help-text">Shown on the Admissions page. Also editable under School Settings.</p>
            </div>

            <div className="form-group">
              <span className="field-label" id="eligible-grades-label">Eligible Grades</span>
              <p className="help-text">Only ticked grades can be chosen on the application form.</p>
              {gradesError && <div className="alert alert-error">The grade list could not be loaded.</div>}
              {grades === null && !gradesError && <p>Loading grades...</p>}
              {grades !== null && ['montessori', 'secondary'].map((program) => {
                const group = grades.filter((g) => g.program === program);
                if (group.length === 0) return null;
                return (
                  <div key={program} className="grade-group" role="group" aria-labelledby="eligible-grades-label">
                    <div className="grade-group-header">
                      <strong>{program === 'montessori' ? 'Montessori' : 'Secondary'}</strong>
                      {!isViewer && (
                        <span>
                          <button type="button" className="link-button" onClick={() => setGroup(program, true)}>All</button>
                          {' / '}
                          <button type="button" className="link-button" onClick={() => setGroup(program, false)}>None</button>
                        </span>
                      )}
                    </div>
                    <div className="grade-options">
                      {group.map((g) => (
                        <label key={g.key} className="grade-option">
                          <input type="checkbox" checked={eligible.has(g.key)} onChange={() => toggleGrade(g.key)} />
                          {g.label}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            </fieldset>

            {!isViewer && (
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={saving || !ready}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            )}
            {!isViewer && !ready && (gradesError || grades !== null) && (
              <p className="help-text">Waiting for the school settings to load. If this persists, reload the page before saving.</p>
            )}
          </div>

          <div className="preview-panel">
            <h2>Preview</h2>
            <p className="subtitle">How users will see it</p>

            <div className={`preview-box status-${previewStatus?.status}`}>
              <div className="preview-badge">
                {previewStatus?.status === 'open' && '✓ Admissions Open'}
                {previewStatus?.status === 'upcoming' && '⏰ Opening Soon'}
                {previewStatus?.status === 'closed' && '✗ Admissions Closed'}
              </div>

              {previewStatus?.message && (
                <div className="preview-message">
                  {previewStatus.message}
                </div>
              )}

              {settings.show_countdown && previewStatus?.remainingDays !== null && (
                <div className="preview-countdown">
                  {previewStatus.status === 'upcoming' 
                    ? `Opens in ${previewStatus.remainingDays} day${previewStatus.remainingDays !== 1 ? 's' : ''}`
                    : `Closes in ${previewStatus.remainingDays} day${previewStatus.remainingDays !== 1 ? 's' : ''}`
                  }
                </div>
              )}

              {academicYear && (
                <div className="preview-year">Academic year {academicYear}</div>
              )}
              <div className="preview-grades">
                {eligible.size} grade{eligible.size !== 1 ? 's' : ''} open for applications
              </div>

              <div className="preview-form-state">
                {previewStatus?.status === 'open' 
                  ? '📝 Application form is enabled'
                  : '🔒 Application form is disabled'
                }
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
    </AdminLayout>
  );
};

export default AdmissionControls;
